import React, { useState, useRef, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInfoCircle,
    faCheckCircle,
    faCloudUploadAlt,
    faImage,
    faLanguage
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/Button/Button';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import TranslationLayout from '../TranslationLayout/TranslationLayout';
import { subjectSchema, SubjectFormValues } from '../../types/subjectSchemas';
import { subjectApi } from '../../api/subjectApi';
import { Subject } from '../../types/subject';
import styles from './SubjectForm.module.css';

import { LANGUAGES } from '../../constants/languages';

interface SubjectFormProps {
    mode: 'add' | 'edit';
}

const SubjectForm: React.FC<SubjectFormProps> = ({ mode }) => {
    const { subjectSlug } = useParams<{ subjectSlug: string }>();
    const [selectedLang, setSelectedLang] = useState(LANGUAGES[0]);
    const [previews, setPreviews] = useState<{ thumbnail: string | null; apiImage: string | null }>({
        thumbnail: null,
        apiImage: null,
    });

    const thumbnailInputRef = useRef<HTMLInputElement>(null);
    const apiImageInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        control,
        reset,
        formState: { isSubmitting, errors },
    } = useForm<SubjectFormValues>({
        resolver: zodResolver(subjectSchema),
        defaultValues: {
            name: '',
            translations: LANGUAGES.reduce((acc, lang) => ({
                ...acc,
                [lang.code]: { name: '' }
            }), {})
        }
    });

    const [subjectId, setSubjectId] = useState<string | null>(null);

    useEffect(() => {
        const loadSubject = async () => {
            if (mode === 'edit' && subjectSlug) {
                try {
                    const response = await subjectApi.getBySlug(subjectSlug);
                    const subject = response.data;
                    if (subject) {
                        setSubjectId(subject.id);
                        reset({
                            name: subject.name,
                            slug: subject.slug,
                            translations: LANGUAGES.reduce((acc, lang) => ({
                                ...acc,
                                [lang.code]: subject.translations?.[lang.code] || { name: '' }
                            }), {})
                        });
                        setPreviews({
                            thumbnail: subject.thumbnail_url || null,
                            apiImage: subject.api_img_url || null,
                        });
                    }
                } catch (error) {
                    console.error('Error loading subject:', error);
                }
            }
        };
        loadSubject();
    }, [mode, subjectSlug, reset]);

    const translations = useWatch({ control, name: 'translations' }) as Record<string, any> || {};

    const handleFileSelect = (field: 'thumbnail' | 'apiImage', file: File) => {
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviews(prev => ({ ...prev, [field]: url }));
            setValue(field, file, { shouldValidate: true });
        }
    };

    const completedCount = Object.values(translations).filter(
        (t: any) => t?.name && t.name.trim().length > 0
    ).length;

    const [isUploading, setIsUploading] = useState(false);

    const onSubmit = async (data: SubjectFormValues) => {
        try {
            setIsUploading(true);
            let thumbnailUrl = previews.thumbnail && !previews.thumbnail.startsWith('blob:') ? previews.thumbnail : undefined;
            let apiImgUrl = previews.apiImage && !previews.apiImage.startsWith('blob:') ? previews.apiImage : undefined;

            const { uploadImage } = await import('api/upload');
            
            if (data.thumbnail instanceof File) {
                thumbnailUrl = await uploadImage(data.thumbnail, 'subjects');
            }
            if (data.apiImage instanceof File) {
                apiImgUrl = await uploadImage(data.apiImage, 'subjects');
            }

            const payload: Partial<Subject> = {
                name: data.name,
                translations: data.translations,
                thumbnail_url: thumbnailUrl,
                api_img_url: apiImgUrl,
            };

            if (mode === 'add') {
                await subjectApi.create(payload);
                alert('Subject created successfully!');
            } else if (subjectId) {
                await subjectApi.update(subjectId, payload);
                alert('Subject updated successfully!');
            }
            navigate('/subject');
        } catch (error) {
            console.error(`Failed to ${mode} subject:`, error);
            alert(`Failed to ${mode} subject`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            <header className={styles.header}>
                <h1 className={styles.title}>{mode === 'add' ? 'Add New Subject' : 'Edit Subject'}</h1>
                <p className={styles.subtitle}>Configure basic information and multi-language support.</p>
            </header>

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* Basic Information Section */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <FontAwesomeIcon icon={faInfoCircle} className={`${styles.sectionIcon} ${styles.sectionIconPrimary}`} />
                        <h2 className={styles.sectionTitle}>Basic Information</h2>
                    </div>

                    <div className={styles.sectionContent}>
                        <div className={styles.imageUploadGrid} style={{ marginBottom: '2rem' }}>
                            <InputWithLabel
                                label="Subject Name"
                                placeholder="e.g. Advanced Physics"
                                error={errors.name?.message}
                                {...register('name')}
                            />
                            {mode === 'edit' &&
                                <InputWithLabel
                                    label="URL Slug"
                                    placeholder="e.g. advanced-physics"
                                    error={errors.slug?.message}
                                    {...register('slug')}
                                    readOnly
                                    style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                                />
                            }
                        </div>

                        <div className={styles.imageUploadGrid}>
                            {/* Thumbnail Upload */}
                            <div className={styles.uploadContainer}>
                                <label className={styles.uploadLabel}>Thumbnail Image</label>
                                <div
                                    className={styles.thumbnailPlaceholder}
                                    onClick={() => thumbnailInputRef.current?.click()}
                                >
                                    {previews.thumbnail ? (
                                        <img src={previews.thumbnail} alt="Thumbnail" className={styles.previewImage} />
                                    ) : (
                                        <div className={styles.placeholderContent}>
                                            <FontAwesomeIcon icon={faImage} className={styles.placeholderIcon} />
                                            <span className={styles.placeholderText}>Click to upload thumbnail</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        ref={thumbnailInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => e.target.files?.[0] && handleFileSelect('thumbnail', e.target.files[0])}
                                        accept="image/*"
                                    />
                                </div>
                            </div>

                            {/* API Image Upload */}
                            <div className={styles.uploadContainer}>
                                <label className={styles.uploadLabel}>API Image (Meta Data)</label>
                                <div
                                    className={styles.thumbnailPlaceholder}
                                    onClick={() => apiImageInputRef.current?.click()}
                                >
                                    {previews.apiImage ? (
                                        <img src={previews.apiImage} alt="API" className={styles.previewImage} />
                                    ) : (
                                        <div className={styles.placeholderContent}>
                                            <FontAwesomeIcon icon={faCloudUploadAlt} className={styles.placeholderIcon} />
                                            <span className={styles.placeholderText}>Upload API Image</span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        ref={apiImageInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => e.target.files?.[0] && handleFileSelect('apiImage', e.target.files[0])}
                                        accept="image/*"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Translations Section */}
                <TranslationLayout
                    languages={LANGUAGES}
                    selectedLang={selectedLang}
                    onLangSelect={setSelectedLang}
                    translations={translations}
                    completedCount={completedCount}
                >
                    <div className={styles.transField}>
                        <InputWithLabel
                            label={`Subject Name in ${selectedLang.name}`}
                            placeholder="Enter translated subject name"
                            labelClassName={styles.transLabel}
                            {...register(`translations.${selectedLang.code}.name` as any)}
                        />
                    </div>
                </TranslationLayout>

                <div className={styles.formActions}>
                    <Button variant="ghost" type="button" onClick={() => navigate('/subject')}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" isLoading={isSubmitting || isUploading}>
                        {mode === 'add' ? 'Save All Changes' : 'Update Subject'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default SubjectForm;
